import React, { useEffect, useState, useMemo } from "react";

import { Spacing, Colors } from "_styles";
import "./job-card.css";
import { statusCircle } from "./job-styles";

import { getTaskCardStatus, convertShiftTimeStamp, getTimeString, calculateMinuteDifference } from "_utils/functions";
import TaskAccept from "./task-accept";
import { EmployeeJobCardWidth, JobCard30MinLayoutWidth, minute30MinLayoutWidth } from "../timeline-constants";

const AdhocTaskCard = (props) => {

    const [jobStatusLine, setJobStatusLine] = useState("Not Assigned");

    const maxHeight = '45px';
    const minHeight = '22.5px';
    const [height, setHeight] = useState(minHeight);
    const [hover, setHover] = useState(false);

    const [nowDate, setNowDate] = useState(() => new Date());
    const now = getTimeString(nowDate);

    useEffect(() => {
        if (props?.job?.isAdhocTask) {
            const cardStatus = getTaskCardStatus(props?.job);
            setJobStatusLine(cardStatus?.status);
        }
    }, [props.job]);

    // refresh current time every minute while the task is active so the width stays accurate
    useEffect(() => {
        if (!(props.job?.active && !props.job?.completed)) return;
        const tick = () => setNowDate(new Date());
        tick();
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, [props.job?.active, props.job?.completed]);

    const getDateFromTimestamp = (ts) => {
        if (!ts) return null;
        const s = ts.seconds ?? ts._seconds;
        const ns = ts.nanoseconds ?? ts._nanoseconds ?? 0;
        if (typeof s === "number") return new Date(s * 1000 + ns / 1_000_000);
        return null;
    };

    const cardWidth = useMemo(() => {
        if (!props.thirtyMinLayout) return EmployeeJobCardWidth + 10;

        const job = props.job;
        const startDate = getDateFromTimestamp(job?.startTime);
        const startTimeString = startDate ? getTimeString(startDate) : job?.assignedTime;

        if (!startTimeString) return JobCard30MinLayoutWidth;

        // Active: width grows from start to now
        if (job?.active && !job?.completed) {
            let widthInMinutes;
            if (startDate) {
                widthInMinutes = Math.max(0, (nowDate.getTime() - startDate.getTime()) / 60000);
            } else {
                if (startTimeString > now) return JobCard30MinLayoutWidth;
                widthInMinutes = calculateMinuteDifference(startTimeString, now);
            }
            return Math.min(Math.max(widthInMinutes * minute30MinLayoutWidth, 10), 720 * minute30MinLayoutWidth) + 10;
        }

        // Completed: width from start to complete
        if (job?.completed && job?.completeTime) {
            const finishDate = getDateFromTimestamp(job.completeTime);
            if (finishDate && startDate) {
                const widthInMinutes = Math.max(0, (finishDate.getTime() - startDate.getTime()) / 60000);
                return Math.min(Math.max(widthInMinutes * minute30MinLayoutWidth, 10), 720 * minute30MinLayoutWidth);
            }
            if (startTimeString) {
                const endTimeString = finishDate ? getTimeString(finishDate) : null;
                if (endTimeString) {
                    const widthInMinutes = calculateMinuteDifference(startTimeString, endTimeString);
                    return Math.min(Math.max(widthInMinutes * minute30MinLayoutWidth, 10), 720 * minute30MinLayoutWidth);
                }
            }
        }

        return EmployeeJobCardWidth;
    }, [props.job, props.thirtyMinLayout, now, nowDate]);

    const intervalWidth = minute30MinLayoutWidth * 5;

    const displayAdhocTaskTimes = () => {
        if (!props.job) return "--:--";

        if (props.job.completed) {
            const start = convertShiftTimeStamp(props.job.startTime);
            const end = convertShiftTimeStamp(props.job.completeTime);
            return `${start}-${end}`;
        }
        if (props.job.stopped) {
            const start = convertShiftTimeStamp(props.job.startTime);
            const stop = convertShiftTimeStamp(props.job.stopTime);
            return `${start}-${stop}`;
        }
        if (props.job.active) {
            return convertShiftTimeStamp(props.job.startTime);
        }
        if (props.job.pending) {
            return convertShiftTimeStamp(props.job.allocatedTime);
        }
        return props.job.assignedTime || "--:--";
    };

    const statusColor = props?.job?.cancelled
        ? 'red'
        : props?.job?.completed
            ? Colors.GREEN
            : props?.job?.isAtLocation
                ? '#2196F3'
                : 'grey';

    return (
        <>
            <div
                onContextMenu={(e) => { props.handleLeftClick(e); }}
                onDoubleClick={(e) => { props.handleLeftClick(e); }}
                onClick={(e) => { props.handleJobClick(e); }}
                onMouseOver={() => { setHeight(maxHeight); setHover(true); }}
                onMouseOut={() => { setHeight(minHeight); setHover(false); }}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: 'relative',
                    overflowY: 'hidden',
                    borderWidth: '1px',
                    borderColor: 'lightgray',
                    borderRadius: 5,
                    color: 'black',
                    backgroundColor: '#f0f0f0',
                    cursor: 'pointer',
                    width: props?.thirtyMinLayout && props?.disabled ? cardWidth : EmployeeJobCardWidth + 10,
                    minWidth: props?.thirtyMinLayout ? (hover ? EmployeeJobCardWidth : intervalWidth) : EmployeeJobCardWidth,
                    height: props?.showFullCard ? maxHeight : props?.thirtyMinLayout ? height : maxHeight,
                    opacity: hover ? 1.0 : props?.job?.completed ? 0.4 : 1,
                }}
            >

                {/* header */}
                <div style={{
                    backgroundColor: Colors.SUNFLOWER_YELLOW,
                    padding: Spacing.SCALE_4,
                    overflow: 'hidden',
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: '600', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {props?.job?.adhocTaskInformation?.title ? `${props.job.adhocTaskInformation.title}` : ''}
                        </p>
                        <div style={{
                            ...statusCircle,
                            backgroundColor: statusColor,
                            border: '1.5px solid white',
                            flexShrink: 0,
                        }} />
                    </div>

                    {/* task type */}
                    <p style={{
                        margin: 0,
                        fontSize: 10,
                        fontWeight: '500',
                        color: '#555',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {props?.job?.adhocTaskInformation?.title || ''}
                    </p>
                </div>

                {/* expanded detail row */}
                {(props?.showFullCard || !props?.thirtyMinLayout || height === maxHeight) && (
                    <>
                        <hr style={{ margin: 0 }} />

                        <div style={{
                            padding: Spacing.SCALE_4,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: 'white',
                        }}>
                            <p style={{ margin: 0, fontSize: 10, fontWeight: "500" }}>
                                {displayAdhocTaskTimes()}
                            </p>

                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ ...statusCircle, backgroundColor: statusColor }} />
                                <p style={{
                                    margin: 0,
                                    textAlign: "right",
                                    fontSize: 10,
                                    color: statusColor,
                                    fontWeight: "700",
                                    whiteSpace: 'nowrap',
                                    marginLeft: Spacing.SCALE_2,
                                }}>
                                    {jobStatusLine}
                                </p>
                            </div>
                        </div>
                    </>
                )}

            </div>

            <TaskAccept
                task={props?.job}
                closeModal={props?.closeModal}
                toggleAcceptModal={props?.toggleAcceptModal}
            />
        </>
    );
};

export default AdhocTaskCard;